
import { useState } from 'react';
import { GameState, ArtistPersonality } from '@/types/career-sim';

export const useGameUI = (
  gameState: GameState,
  updateGameState: (updates: Partial<GameState>) => void
) => {
  const [activeTab, setActiveTab] = useState('actions');
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeGame, setActiveGame] = useState<string | null>(null);
  const [gameDifficulty, setGameDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const [gameOpponent, setGameOpponent] = useState<'ai' | 'player'>('ai');
  
  const handleStartAction = (action: any) => {
    updateGameState({
      activeAction: action
    });
  };
  
  const handleClearNotifications = () => {
    updateGameState({
      notifications: []
    });
  };
  
  const handleMarkAllNotificationsRead = () => {
    updateGameState({
      notifications: gameState.notifications.map(notification => ({
        ...notification,
        read: true
      }))
    });
  };
  
  const handleStartChallenge = (challengeId: string) => {
    const updatedChallenges = gameState.challenges.map(challenge => {
      if (challenge.id === challengeId) {
        return {
          ...challenge,
          inProgress: true
        };
      }
      return challenge;
    });
    
    updateGameState({
      challenges: updatedChallenges
    });
  };
  
  const handleSelectGame = (gameId: string) => {
    setActiveGame(gameId);
    setActiveTab('games');
  };
  
  const handleGameComplete = (score: number, maxScore: number) => {
    const ratio = score / maxScore;
    
    const difficultyMultiplier = gameDifficulty === 'easy' ? 1 : gameDifficulty === 'medium' ? 2 : 3;
    
    let fundsReward = 0;
    let reputationReward = 0;
    let fansReward = 0;
    
    if (activeGame === 'familyfeud') {
      fundsReward = Math.floor(50 * ratio * difficultyMultiplier);
      reputationReward = Math.floor(10 * ratio * difficultyMultiplier);
      fansReward = Math.floor(20 * ratio * difficultyMultiplier);
    } else if (activeGame === 'trivia') {
      fundsReward = Math.floor(30 * ratio * difficultyMultiplier);
      reputationReward = Math.floor(15 * ratio * difficultyMultiplier);
      fansReward = Math.floor(10 * ratio * difficultyMultiplier);
    } else if (activeGame === 'marketing') {
      fundsReward = Math.floor(80 * ratio * difficultyMultiplier);
      reputationReward = Math.floor(20 * ratio * difficultyMultiplier);
      fansReward = Math.floor(50 * ratio * difficultyMultiplier);
    } else if (activeGame === 'pitchdeck') {
      fundsReward = Math.floor(100 * ratio * difficultyMultiplier);
      reputationReward = Math.floor(25 * ratio * difficultyMultiplier);
      fansReward = Math.floor(30 * ratio * difficultyMultiplier);
    }
    
    updateGameState({
      career: {
        ...gameState.career,
        funds: gameState.career.funds + fundsReward,
        reputation: gameState.career.reputation + reputationReward,
        fanbase: gameState.career.fanbase + fansReward
      },
      notifications: [
        ...gameState.notifications,
        {
          id: `game-reward-${Date.now()}`,
          message: `Game Rewards: +$${fundsReward}, +${reputationReward} reputation, +${fansReward} fans`,
          type: 'success',
          read: false,
          timestamp: new Date()
        }
      ]
    });
  };

  return {
    activeTab,
    setActiveTab,
    showNotifications,
    setShowNotifications,
    activeGame,
    setActiveGame,
    gameDifficulty,
    setGameDifficulty,
    gameOpponent,
    setGameOpponent,
    handleStartAction,
    handleClearNotifications,
    handleMarkAllNotificationsRead,
    handleStartChallenge,
    handleSelectGame,
    handleGameComplete
  };
};
